import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {
  ProcessExecutionType,
  ProcessFlowchartPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import * as logging from 'util/Logging';

import {
  ProcessFlowchartTypeConfiguratorQuery,
  ProcessFlowchartTypeConfiguratorQueryVariables,
} from './__generated/ProcessFlowchartTypeConfiguratorQuery';

// processFlowchartTypeConfigurator uses flowchartTypeName as the key field, see typePolicies.ts
export const PROCESS_FLOWCHART_TYPE_CONFIGURATOR_QUERY: DocumentNode = gql`
  query ProcessFlowchartTypeConfiguratorQuery($flowchartTypeName: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processFlowchartTypeConfigurator(flowchartTypeName: $flowchartTypeName) {
      flowchartTypeName
      allowableTaskTypes
      platformFlowchartFeatures {
        feature
        enabled
      }
    }
  }
`;

/**
 * Provider of configuration for a type of flowchart used in the flowchart editor.
 *
 * A type of flowchart has two main kinds of configuration:
 * - The allowable node types for the flowchart.
 * - Platform features that are enabled for the flowchart.
 *
 * Usually, you shouldn't instantiate this class directly. Instead, use the
 * `useProcessFlowchartEditorConfigurator` hook to get an instance of this class.
 *
 */
export class ProcessFlowchartEditorConfigurator {
  constructor(
    public readonly flowchartTypeName: string,
    public readonly allowedNodeExecutionTypes: Array<ProcessExecutionType>,
    public readonly platformFlowchartFeatures: Map<ProcessFlowchartPlatformFeatureType, boolean>
  ) {}

  /**
   * Returns whether a platform feature is enabled for this flowchart type.
   *
   * @param feature - The platform feature to check.
   * @returns Whether the feature is enabled.
   */
  hasFeature(feature: ProcessFlowchartPlatformFeatureType): boolean {
    return this.platformFlowchartFeatures.get(feature) ?? false;
  }
}

/**
 * The type of result of a call to `useProcessFlowchartEditorConfigurator`.
 */
export enum ConfiguratorResultType {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export type ProcessFlowchartEditorConfiguratorResult =
  | {
      resultType: ConfiguratorResultType.PENDING;
      configurator: null;
      error: undefined;
    }
  | {
      resultType: ConfiguratorResultType.SUCCESS;
      configurator: ProcessFlowchartEditorConfigurator;
      error: undefined;
    }
  | {
      resultType: ConfiguratorResultType.ERROR;
      configurator: null;
      error: Error;
    };

/**
 * Hook to get an instance of the `ProcessFlowchartEditorConfigurator` for a given flowchart type.
 *
 * @param flowchartTypeName - The name of the flowchart type to get the configurator for.
 * @returns The configurator for the flowchart type, or `null` if the configurator is not found
 * or is still loading.
 */
export function useProcessFlowchartEditorConfigurator(
  flowchartTypeName: string,
  options?: {skip?: boolean}
): ProcessFlowchartEditorConfiguratorResult {
  const configuratorResult = useSkippableBenchlingQuery<
    ProcessFlowchartTypeConfiguratorQuery,
    ProcessFlowchartTypeConfiguratorQueryVariables
  >(PROCESS_FLOWCHART_TYPE_CONFIGURATOR_QUERY, {
    variables: {flowchartTypeName},
    skip: options?.skip ?? false,
  });

  if (configuratorResult.type === QueryResultType.SKIPPED) {
    return {
      resultType: ConfiguratorResultType.PENDING,
      configurator: null,
      error: undefined,
    };
  }

  if (configuratorResult.type === QueryResultType.ERROR) {
    return {
      resultType: ConfiguratorResultType.ERROR,
      configurator: null,
      error:
        configuratorResult.error ??
        new Error(`Unknown error while retrieving configurator of type ${flowchartTypeName}.`),
    };
  }

  if (configuratorResult.type === QueryResultType.PENDING) {
    return {
      resultType: ConfiguratorResultType.PENDING,
      configurator: null,
      error: undefined,
    };
  }

  const platformFlowchartFeatures = new Map<ProcessFlowchartPlatformFeatureType, boolean>();
  configuratorResult.data.processFlowchartTypeConfigurator.platformFlowchartFeatures.forEach((feature) => {
    platformFlowchartFeatures.set(feature.feature, feature.enabled);
  });

  const allowableTaskTypes = configuratorResult.data.processFlowchartTypeConfigurator.allowableTaskTypes
    .map((execTypeName) => {
      const executionType = ProcessExecutionType[execTypeName as keyof typeof ProcessExecutionType];
      if (executionType === undefined) {
        logging.warn(
          `Unknown execution type '${execTypeName}' returned from backend for flowchart type '${flowchartTypeName}'`
        );
      }
      return executionType;
    })
    .filter((execType): execType is ProcessExecutionType => execType !== undefined);

  return {
    resultType: ConfiguratorResultType.SUCCESS,
    configurator: new ProcessFlowchartEditorConfigurator(
      flowchartTypeName,
      allowableTaskTypes,
      platformFlowchartFeatures
    ),
    error: undefined,
  };
}
